import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  addTask,
  allTasks,
  emptyTasks,
  getTasksDoc,
  moveTask,
  setArchived,
  updateTask,
} from '../../db/tasks';
import { useAsync } from '../hooks/useAsync';
import { shouldSubmitOnEnter } from './CommitInput';
import { useDataVersion } from '../../db/events';
import {
  TASK_AGGREGATE_LABELS,
  TASK_TYPE_LABELS,
  type TaskAggregate,
  type TaskDef,
  type TaskKind,
  type TasksDoc,
  type TaskValueType,
} from '../../domain/types';

type Draft = {
  id?: string;
  kind: TaskKind;
  name: string;
  type: TaskValueType;
  unit: string;
  aggregate: TaskAggregate;
  target: string;
};

const blank = (kind: TaskKind): Draft => ({
  kind,
  name: '',
  type: 'check',
  unit: '',
  aggregate: 'sum',
  target: '',
});

export function TaskManager() {
  const version = useDataVersion();
  const doc = useAsync<TasksDoc>(getTasksDoc, [version], emptyTasks());
  const [draft, setDraft] = useState<Draft | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const tasks = allTasks(doc);
  const active = tasks.filter((t) => !t.archived);
  const archived = tasks.filter((t) => t.archived);

  const submit = async () => {
    if (!draft || draft.name.trim() === '') return;
    const target = draft.target.trim() === '' ? null : Number(draft.target);
    const payload = {
      kind: draft.kind,
      name: draft.name.trim(),
      type: draft.type,
      unit: draft.type === 'number' ? draft.unit.trim() || undefined : undefined,
      aggregate: draft.type === 'number' ? draft.aggregate : undefined,
      target: Number.isFinite(target as number) ? target : null,
    };
    if (draft.id) await updateTask(draft.id, payload);
    else await addTask(payload);
    setDraft(null);
  };

  const row = (t: TaskDef, i: number, list: TaskDef[]) => (
    <li key={t.id} className={t.archived ? 'is-deleted' : ''}>
      <span className={`task-kind-badge${t.kind === 'weekly' ? ' weekly' : ''}`}>
        {t.kind === 'daily' ? '毎日' : '毎週'}
      </span>
      <span className="cat-name">{t.name}</span>
      <span className="muted cat-count">
        {TASK_TYPE_LABELS[t.type]}
        {t.unit ? `・${t.unit}` : ''}
      </span>
      <span className="cat-actions">
        {!t.archived && (
          <>
            <button className="icon-btn" onClick={() => moveTask(t.id, -1)} disabled={i === 0} aria-label="上へ">↑</button>
            <button className="icon-btn" onClick={() => moveTask(t.id, 1)} disabled={i === list.length - 1} aria-label="下へ">↓</button>
            <button
              className="icon-btn"
              aria-label="編集"
              onClick={() =>
                setDraft({
                  id: t.id,
                  kind: t.kind,
                  name: t.name,
                  type: t.type,
                  unit: t.unit ?? '',
                  aggregate: t.aggregate ?? 'sum',
                  target: t.target != null ? String(t.target) : '',
                })
              }
            >
              ✎
            </button>
          </>
        )}
        <button
          className={t.archived ? 'icon-btn' : 'icon-btn danger'}
          aria-label={t.archived ? '再開' : '停止'}
          onClick={() => {
            if (t.archived) return void setArchived(t.id, false);
            if (confirm(`「${t.name}」を停止します。入力欄から外れますが、記録とレポートは残ります。`)) {
              setArchived(t.id, true);
            }
          }}
        >
          {t.archived ? '再開' : '停止'}
        </button>
      </span>
    </li>
  );

  return (
    <>
      {active.length === 0 ? (
        <p className="muted">タスクがまだありません。追加すると、日ビュー・週ビューに入力欄が並びます。</p>
      ) : (
        <ul className="cat-list">
          {active.map((t) => {
            const siblings = active.filter((x) => x.kind === t.kind);
            return row(t, siblings.indexOf(t), siblings);
          })}
        </ul>
      )}

      <div className="btn-row">
        <button className="ghost-btn" onClick={() => setDraft(blank('daily'))}>
          ＋ デイリータスク
        </button>
        <button className="ghost-btn" onClick={() => setDraft(blank('weekly'))}>
          ＋ ウィークリータスク
        </button>
        <Link className="ghost-btn" to="/reports">
          レポートを見る
        </Link>
        {archived.length > 0 && (
          <button className="ghost-btn" onClick={() => setShowArchived((v) => !v)}>
            停止中（{archived.length}）を{showArchived ? '隠す' : '表示'}
          </button>
        )}
      </div>

      {showArchived && archived.length > 0 && (
        <ul className="cat-list deleted-list">{archived.map((t, i) => row(t, i, archived))}</ul>
      )}

      {draft && (
        <div className="modal-backdrop" onClick={() => setDraft(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              {draft.id ? 'タスクを編集' : draft.kind === 'daily' ? 'デイリータスクを追加' : 'ウィークリータスクを追加'}
            </h3>

            <label className="field">
              <span className="field-label">名前</span>
              <input
                type="text"
                autoFocus
                maxLength={40}
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                onKeyDown={(e) => shouldSubmitOnEnter(e) && submit()}
              />
            </label>

            <label className="field">
              <span className="field-label">入力の種類</span>
              <select
                value={draft.type}
                onChange={(e) => setDraft({ ...draft, type: e.target.value as TaskValueType })}
              >
                {(['check', 'number', 'text'] as TaskValueType[]).map((t) => (
                  <option key={t} value={t}>
                    {TASK_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </label>

            {draft.type === 'number' && (
              <>
                <label className="field">
                  <span className="field-label">単位（任意）</span>
                  <input
                    type="text"
                    maxLength={8}
                    placeholder="kg / 円 / 回"
                    value={draft.unit}
                    onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span className="field-label">期間の集計方法</span>
                  <select
                    value={draft.aggregate}
                    onChange={(e) => setDraft({ ...draft, aggregate: e.target.value as TaskAggregate })}
                  >
                    {(['sum', 'avg', 'last'] as TaskAggregate[]).map((a) => (
                      <option key={a} value={a}>
                        {TASK_AGGREGATE_LABELS[a]}
                      </option>
                    ))}
                  </select>
                  <span className="field-hint">
                    歩数や売上は「合計」、体重や睡眠時間は「平均」が向いています
                  </span>
                </label>
                <label className="field">
                  <span className="field-label">1回あたりの目標（任意）</span>
                  <input
                    type="number"
                    step="any"
                    value={draft.target}
                    onChange={(e) => setDraft({ ...draft, target: e.target.value })}
                  />
                </label>
              </>
            )}

            <div className="btn-row modal-actions">
              <button className="ghost-btn" onClick={() => setDraft(null)}>
                キャンセル
              </button>
              <button className="primary-btn" onClick={submit} disabled={draft.name.trim() === ''}>
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
